import { FormattedMessage } from 'react-intl';
import { Salary } from '@tink/core/src/components/atoms/Icons/Salary';
import { Spacer } from '@tink/core/src/components/atoms/Spacer/Spacer';
import { Profile } from '@tink/core/src/components/atoms/Icons/Profile';
import { CircledIcon } from '../../../components/atoms/Icons/CircledIcon';
import { Header } from '../../../components/organisms/Header/Header';
import { H1 } from '../../../components/atoms/Heading/Heading';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { SelectableOption } from '../../../components/screens/molecules/SelectableOption/SelectableOption';
import { Subtitle } from '../../../components/atoms/Subtitle/Subtitle';
import {
  FinancialServicesSegment,
  ProviderAccessType,
  ProviderEntity,
  ProviderFinancialService,
} from '../../../models/provider';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { PageView } from '../../../models/analytics';

import { messages } from '../AuthenticationTypePicker/messages';

export const getUniqueFinancialServices = (providers: ProviderEntity[]) => {
  const financialServices = providers
    .map((provider) => provider.financialServices)
    .filter(Boolean)
    .flat() as ProviderFinancialService[];
  const uniqueFinancialServices: ProviderFinancialService[] = [];

  financialServices.forEach((financialService) => {
    const alreadyExists = uniqueFinancialServices.find(
      (ufs) =>
        ufs.segment === financialService.segment && ufs.shortName === financialService.shortName
    );

    if (!alreadyExists) {
      uniqueFinancialServices.push(financialService);
    }
  });

  return uniqueFinancialServices;
};

type OnProviderItemClickPayload = {
  accessType: ProviderAccessType;
  financialServicesSegment: FinancialServicesSegment;
};

type OnProviderItemClick = (payload: OnProviderItemClickPayload) => void;

type FinancialServicePickerProps = {
  onBack: () => void;
  providers: ProviderEntity[];
  onProviderItemClick: OnProviderItemClick;
};

export const FinancialServicePicker = ({
  onBack,
  providers,
  onProviderItemClick,
}: FinancialServicePickerProps) => {
  const uniqueFinancialServices: ProviderFinancialService[] = getUniqueFinancialServices(providers);

  return (
    <ScreenWrapper>
      <Header onBack={onBack} pageView={PageView.FINANCIAL_SERVICE_PICKER_SCREEN} />
      <ContentBody>
        <H1>
          <FormattedMessage {...messages.loginType} />
        </H1>
        <Subtitle>
          <FormattedMessage {...messages.whichWayToLogin} />
        </Subtitle>
        <Spacer size="normal" />
        {uniqueFinancialServices.map((uniqueFinancialService) => {
          const icon =
            uniqueFinancialService.segment === FinancialServicesSegment.PERSONAL ? (
              <CircledIcon Icon={Profile} />
            ) : (
              <CircledIcon Icon={Salary} />
            );

          const selectableOptionText =
            uniqueFinancialService.shortName || uniqueFinancialService.segment.toLowerCase();
          const filteredProviders = providers.filter((p) => {
            const financialService = p.financialServices?.find(
              (fs) =>
                fs.shortName === uniqueFinancialService.shortName ||
                fs.segment === uniqueFinancialService.segment
            );

            return Boolean(financialService);
          });
          const openBankingProvider = filteredProviders.find(
            (provider) => provider.accessType === ProviderAccessType.OPEN_BANKING
          );

          return (
            <SelectableOption
              Icon={icon}
              key={`${uniqueFinancialService.shortName}-${uniqueFinancialService.segment}`}
              text={selectableOptionText}
              onClick={() => {
                onProviderItemClick({
                  accessType: openBankingProvider
                    ? ProviderAccessType.OPEN_BANKING
                    : ProviderAccessType.OTHER,
                  financialServicesSegment: uniqueFinancialService.segment,
                });
              }}
            />
          );
        })}
      </ContentBody>
    </ScreenWrapper>
  );
};
